import { InputDecimal } from '@/components/_common';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import ManufacturerColumnFilter from '@/components/_common/Manufacturer/ManufacturerColumnFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import StatusTag from '@/components/_common/StatusTag';
import SubGroupColumnFilter from '@/components/_common/SubGroup/SubGroupColumnFilter';
import SubGroupsSelectField from '@/components/_common/SubGroupsSelectField';
import SubAreasColumnFilter from '@/components/_common/SubAreasColumnFilter';
import {
  NewCiliaItem,
  UpdatedCiliaItem,
} from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import {
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Table, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';

interface Props {
  data: NewCiliaItem[];
  setUpdateData: React.Dispatch<
    React.SetStateAction<{
      updateItems: UpdatedCiliaItem[];
      newItems: NewCiliaItem[];
    } | null>
  >;
  setSelectedNewItems: React.Dispatch<React.SetStateAction<NewCiliaItem[]>>;
}

export default function NewItemsList({
  data,
  setUpdateData,
  setSelectedNewItems,
}: Props) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filterGroupOpen, setFilterGroupOpen] = useState(false);
  const [filterManufactureOpen, setFilterManufactureOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  function handleChange(
    field: keyof NewCiliaItem,
    index: number,
    value?: string | number,
  ) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const newItems = [...prev.newItems];
      newItems[index] = {
        ...newItems[index],
        [field]: value,
      };

      const selected = newItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedNewItems(selected);
      return {
        ...prev,
        newItems,
      };
    });
  }

  function setSubAreaId(subAreaId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const newItems = prev.newItems.map((item) => ({
        ...item,
        subAreaId,
      }));

      const selected = newItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedNewItems(selected);
      return {
        ...prev,
        newItems,
      };
    });

    setFilterDropdownOpen(false);
  }

  function setSubGroupId(subGroupId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const newItems = prev.newItems.map((item) =>
        !item.itemId
          ? {
              ...item,
              subGroupId:
                item.type === WorkOrderItemType.Part ? subGroupId : undefined,
            }
          : item,
      );

      const selected = newItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedNewItems(selected);
      return {
        ...prev,
        newItems,
      };
    });

    setFilterGroupOpen(false);
  }

  function setManufacturerId(manufacturerId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const newItems = prev.newItems.map((item) =>
        !item.itemId
          ? {
              ...item,
              manufacturerId:
                item.type === WorkOrderItemType.Part
                  ? manufacturerId
                  : undefined,
            }
          : item,
      );

      const selected = newItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedNewItems(selected);

      return {
        ...prev,
        newItems,
      };
    });

    setFilterManufactureOpen(false);
  }

  useEffect(() => {
    const selected = selectedRowKeys.map((key) => data[Number(key)]);
    setSelectedNewItems(selected);
  }, [selectedRowKeys]);

  useEffect(() => {
    const allKeys = data.map((_, index) => String(index));
    setSelectedRowKeys(allKeys);
    setSelectedNewItems(data);
  }, []);

  return (
    <>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Novos Itens
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Itens novos detectados na importação, que ainda não existem na OS e
        serão adicionados.
      </Typography.Paragraph>

      <Table
        dataSource={data}
        pagination={false}
        rowKey={(_, index) => String(index)}
        scroll={{ x: true }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            dataIndex: 'description',
            title: 'Descrição',
            width: 250,
            ellipsis: true,
          },
          {
            dataIndex: 'type',
            title: 'Tipo',
            width: 80,
            ellipsis: true,
            render: (_, item) =>
              item.type === WorkOrderItemType.Part ||
              item.type === WorkOrderItemType.Service ? (
                <StatusTag
                  color={workorderTypeColor[item.type]}
                  label={
                    item.type === WorkOrderItemType.Service ? 'Serviço' : 'Peça'
                  }
                />
              ) : (
                '-'
              ),
          },
          {
            dataIndex: 'subAreaId',
            title: 'Área',
            width: 200,
            filterDropdownOpen,
            onFilterDropdownOpenChange: setFilterDropdownOpen,
            filterDropdown: () => (
              <SubAreasColumnFilter onChange={setSubAreaId} />
            ),
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, _, index) => (
              <AreasInfiniteSelectField
                value={rowValue}
                style={{ width: 200 }}
                onChange={(value) => handleChange('subAreaId', index, value)}
                queryFilter={{ active: true }}
              />
            ),
          },
          {
            dataIndex: 'subGroupId',
            title: 'Grupo',
            width: 200,
            filterDropdownOpen: filterGroupOpen,
            onFilterDropdownOpenChange: setFilterGroupOpen,
            filterDropdown: () => (
              <SubGroupColumnFilter onChange={setSubGroupId} />
            ),
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, row, index) => (
              <SubGroupsSelectField
                value={rowValue}
                style={{ width: 200 }}
                disabled={
                  !!row.itemId || row.type === WorkOrderItemType.Service
                }
                onChange={(value) => handleChange('subGroupId', index, value)}
              />
            ),
          },
          {
            dataIndex: 'manufacturerId',
            title: 'Fabricante',
            width: 200,
            filterDropdownOpen: filterManufactureOpen,
            onFilterDropdownOpenChange: setFilterManufactureOpen,
            filterDropdown: () => (
              <ManufacturerColumnFilter onChange={setManufacturerId} />
            ),
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, row, index) => (
              <ManufacturerInfiniteSelectField
                value={rowValue}
                disabled={
                  !!row.itemId || row.type === WorkOrderItemType.Service
                }
                style={{ width: 200 }}
                onChange={(value) =>
                  handleChange('manufacturerId', index, value)
                }
              />
            ),
          },
          {
            dataIndex: 'unitPrice',
            title: 'Valor unitário',
            width: 150,
            render: (rowValue, _, index) => (
              <InputDecimal
                style={{ width: 200 }}
                value={rowValue}
                onChange={(value) => handleChange('unitPrice', index, value)}
              />
            ),
          },
        ]}
      />
    </>
  );
}
